/**
 * TanStack Query hook for the Space Yield Report.
 *
 * Runs 3 parallel Supabase queries (units, leases, transient stays),
 * then merges + computes the report client-side.
 */

import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase'
import { startOfMonth, endOfMonth, format, parseISO } from 'date-fns'
import { computeReport } from '@/lib/reports/space-yield/compute'
import type {
  RawUnit,
  RawLease,
  RawTransientStay,
  RawFuelTransaction,
  RawAllocationWithFootprint,
  SpaceYieldReport,
  ReportParams,
} from '@/lib/reports/space-yield/types'

export const spaceYieldKeys = {
  all: ['space-yield'] as const,
  report: (locationId: string, params: ReportParams) =>
    [...spaceYieldKeys.all, locationId, params] as const,
}

async function fetchSpaceYieldData(
  locationId: string,
  period: string,
): Promise<{ units: RawUnit[]; leases: RawLease[]; transients: RawTransientStay[]; fuelTransactions: RawFuelTransaction[]; allocations: RawAllocationWithFootprint[] }> {
  const monthStart = format(startOfMonth(parseISO(`${period}-01`)), 'yyyy-MM-dd')
  const monthEnd = format(endOfMonth(parseISO(`${period}-01`)), 'yyyy-MM-dd')

  // Run 5 queries in parallel
  const [unitsResult, leasesResult, transientsResult, fuelResult, allocationsResult] = await Promise.all([
    // Query 1: Units + their area (building)
    supabase
      .from('units')
      .select(`
        id,
        unit_code,
        area_id,
        location_id,
        type,
        capacity_strategy,
        usable_sqft,
        door_width,
        depth,
        status,
        is_leasable,
        areas!area_id (
          id,
          name,
          type,
          total_footprint_sqft
        )
      `)
      .eq('location_id', locationId)
      .is('deleted_at', null)
      .neq('status', 'archived'),

    // Query 2: Active leases overlapping the period (with escalation + renewal fields)
    supabase
      .from('lease_contracts')
      .select(`
        id,
        unit_id,
        client_id,
        monthly_rate,
        type,
        start_date,
        end_date,
        status,
        contract_number,
        escalation_type,
        escalation_pct,
        auto_renew,
        renewal_contacted_date,
        fuel_commitment_gallons_monthly,
        fuel_commitment_gallons_annual,
        fuel_price_per_gallon,
        fuel_type,
        clients!client_id (
          id,
          company_name,
          first_name,
          last_name
        )
      `)
      .eq('location_id', locationId)
      .in('status', ['active', 'expiring'])
      .lte('start_date', monthEnd)
      .or(`end_date.is.null,end_date.gte.${monthStart}`)
      .is('deleted_at', null),

    // Query 3: Transient stays overlapping the period
    supabase
      .from('transient_stays')
      .select(`
        id,
        unit_id,
        client_id,
        subtotal,
        nights_count,
        check_in,
        expected_check_out,
        actual_check_out,
        status,
        clients!client_id (
          id,
          company_name,
          first_name,
          last_name
        )
      `)
      .eq('location_id', locationId)
      .in('status', ['checked_in', 'checked_out'])
      .not('unit_id', 'is', null)
      .lte('check_in', monthEnd)
      .is('deleted_at', null),

    // Query 4: Fuel transactions in the period
    supabase
      .from('fuel_transactions')
      .select('id, lease_contract_id, transient_stay_id, total_amount, quantity_gallons, fueled_at')
      .eq('location_id', locationId)
      .eq('status', 'completed')
      .gte('fueled_at', monthStart)
      .lte('fueled_at', monthEnd)
      .is('deleted_at', null),

    // Query 5: Active allocations with aircraft footprints (for occupancy calculation)
    supabase
      .from('allocations')
      .select(`
        id,
        unit_id,
        aircraft_id,
        aircraft!aircraft_id (
          id,
          aircraft_type_id,
          aircraft_types!aircraft_type_id (
            footprint_sqft,
            wingspan,
            length
          )
        )
      `)
      .eq('location_id', locationId)
      .in('status', ['active', 'reserved'])
      .not('unit_id', 'is', null)
      .is('deleted_at', null),
  ])

  if (unitsResult.error) throw unitsResult.error
  if (leasesResult.error) throw leasesResult.error
  if (transientsResult.error) throw transientsResult.error
  if (fuelResult.error) throw fuelResult.error
  if (allocationsResult.error) throw allocationsResult.error

  // Filter transients client-side: COALESCE(actual_check_out, expected_check_out) >= monthStart
  const filteredTransients = (transientsResult.data as unknown as RawTransientStay[]).filter((ts) => {
    const endDate = ts.actual_check_out || ts.expected_check_out
    return endDate >= monthStart
  })

  return {
    units: unitsResult.data as unknown as RawUnit[],
    leases: leasesResult.data as unknown as RawLease[],
    transients: filteredTransients,
    fuelTransactions: fuelResult.data as unknown as RawFuelTransaction[],
    allocations: allocationsResult.data as unknown as RawAllocationWithFootprint[],
  }
}

export function useSpaceYieldReport(
  locationId: string | undefined,
  params: ReportParams,
): { data: SpaceYieldReport | undefined; isLoading: boolean; error: Error | null } {
  const { currentOrganization } = useTenant()

  const result = useQuery({
    queryKey: spaceYieldKeys.report(locationId || '', params),
    queryFn: async () => {
      const raw = await fetchSpaceYieldData(locationId!, params.period)

      return computeReport(
        raw.units,
        raw.leases,
        raw.transients,
        raw.fuelTransactions,
        raw.allocations,
        params.period,
        params.types,
        params.segment,
        params.group_by,
        params.status,
      )
    },
    enabled: !!locationId && !!currentOrganization,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  return {
    data: result.data,
    isLoading: result.isLoading,
    error: result.error as Error | null,
  }
}
